import { 
  selectRiskSource
} from '../../redux/actions/riskActions';
import { promoteScenarioToSaved } from '../../redux/actions/riskScenarioActions';
import { v4 as uuid } from 'uuid';

export class RiskDiscoveryServiceV2 {
  constructor(dispatch, modelId, assessmentUuid, bucket, path, owner, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.bucket = bucket;
    this.path = path;
    this.owner = owner;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeDiscovery(areaName, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, progressCallback) {
    try {
      // Task 1: Analyzing Risk Area
      if (progressCallback) progressCallback('analyzing risk area');
      await new Promise(resolve => setTimeout(resolve, 500));

      const areaRes = await fetch('/.netlify/functions/analyzeRiskArea', {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          modelId: this.modelId, 
          assessmentUuid: this.assessmentUuid, 
          bucket: this.bucket, 
          path: this.path, 
          areaName, 
          areaDefinition, 
          contextSummaries: useCaseAnalysisData
        })
      });
      if (!areaRes.ok) throw new Error(await areaRes.text().catch(() => 'Area analysis failed'));
      const { areaCard } = await areaRes.json();

      // LIVE UPDATE: Area analysis complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('area', { areaCard });
      }

      // Task 2: Researching Risk Sources
      if (progressCallback) progressCallback('researching risk sources');
      await new Promise(resolve => setTimeout(resolve, 800));

      const sourcesRes = await fetch('/.netlify/functions/researchRiskSources', {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          modelId: this.modelId, 
          assessmentUuid: this.assessmentUuid, 
          bucket: this.bucket, 
          path: this.path, 
          areaName, 
          areaDefinition, 
          contextSummaries: useCaseAnalysisData
        })
      });
      if (!sourcesRes.ok) throw new Error(await sourcesRes.text().catch(() => 'Source research failed'));
      const { selectedSources } = await sourcesRes.json();

      // LIVE UPDATE: Source research complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('sources', { selectedSources });
      }

      // Task 3: Researching Root Causes (with timeout bypass)
      if (progressCallback) progressCallback('researching root causes');
      await new Promise(resolve => setTimeout(resolve, 800));

      let causesBySource = {};
      try {
        const causesRes = await fetch('/.netlify/functions/researchRootCauses', {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            modelId: this.modelId, 
            assessmentUuid: this.assessmentUuid, 
            bucket: this.bucket, 
            path: this.path, 
            areaName, 
            areaDefinition,
            selectedSources,
            contextSummaries: useCaseAnalysisData,
            evaluationCriteria: riskCauseEvalCfg
          })
        });
        
        if (causesRes.ok) {
          const data = await causesRes.json();
          causesBySource = data.causesBySource || {};
        } else {
          console.warn('[RiskDiscoveryServiceV2] Root cause research failed, using fallback approach');
          // Generate simplified causes from selected sources
          causesBySource = this.generateFallbackCauses(selectedSources, areaDefinition);
        }
      } catch (causesError) {
        console.warn('[RiskDiscoveryServiceV2] Root cause research timed out, using fallback approach:', causesError.message);
        // Generate simplified causes from selected sources
        causesBySource = this.generateFallbackCauses(selectedSources, areaDefinition);
      }

      // LIVE UPDATE: Cause research complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('causes', { causesBySource });
      }

      // Task 4: Extract Relevant Causes for RAG (Skip Complex Assignment)
      if (progressCallback) progressCallback('preparing causes for RAG');
      await new Promise(resolve => setTimeout(resolve, 200));

      // Extract relevant causes from research results based on confidence
      const relevantCausesForRAG = this.extractRelevantCausesForRAG(selectedSources, causesBySource, useCaseAnalysisData);
      
      console.log(`[RiskDiscoveryServiceV2] Extracted ${relevantCausesForRAG.length} relevant causes for RAG enhancement`);
      
      // LIVE UPDATE: Cause preparation complete
      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('causesForRAG', { 
          relevantCauses: relevantCausesForRAG,
          totalCauseCategories: Object.keys(causesBySource).length
        });
      }
      
      // Persist sources with sufficient evidence to Redux (no complex assignments)
      for (const s of selectedSources || []) {
        if (s.aiConfidence >= 0.5) {
          this.dispatch(selectRiskSource(this.modelId, areaName, { name: s.sourceName }));
        }
      }

      // Task 4: RAG-Powered Scenarios
      if (progressCallback) progressCallback('scenarios');
      await new Promise(resolve => setTimeout(resolve, 1500));

      let scenarios = [];
      let ragMetadata = {};
      try {
        console.log(`[RiskDiscoveryServiceV2] SENDING TO RAG: ${relevantCausesForRAG.length} causes`);
        console.log(`[RiskDiscoveryServiceV2] RAG PAYLOAD relevantCauses:`, relevantCausesForRAG);
        
        // Use co-pilot's RAG scenario generation enhanced with autopilot's cause analysis
        const scenRes = await fetch('/.netlify/functions/generateScenariosWithRAG', {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            card: areaCard,                      // Area analysis card
            modelNarratives: useCaseAnalysisData, // Context narratives
            selectedCatalogues: [],              // Use all available catalogues
            useAllCatalogues: true,              // Let RAG select best catalogues
            // ENHANCED: Pass autopilot's rich context for better RAG matching
            selectedSources: selectedSources,    // Risk sources with confidence
            relevantCauses: relevantCausesForRAG, // Extracted causes for context
            contextEnrichment: true              // Flag to use enhanced context
          })
        });
        
        if (scenRes.ok) {
          const scenData = await scenRes.json();
          scenarios = scenData.scenarios || [];
          
          // Extract RAG metadata for user visibility
          ragMetadata = {
            method: scenData.method || 'RAG',
            cataloguesUsed: scenData.cataloguesUsed || [],
            retrievedRisks: scenData.retrievedRisks || [],
            generationSource: 'RAG-Enhanced'
          };
          
          // Auto-promote ALL generated scenarios to risks for user review (in parallel for performance)
          console.log(`[RiskDiscoveryServiceV2] 🚀 Auto-promoting ${scenarios.length} scenarios to risks (parallel processing)`);
          const promotionStartTime = Date.now();
          
          const promotionPromises = scenarios.map(async (scenario, index) => {
            const scenarioId = uuid();
            console.log(`[RiskDiscoveryServiceV2] [${index + 1}/${scenarios.length}] Promoting: "${scenario.scenarioText?.slice(0, 40)}..." (${scenario.entity}/${scenario.intent}/${scenario.timing})`);
            
            // Distribute scenarios across real user sources (not RAG catalogs)
            const userSourceName = selectedSources?.[index % selectedSources.length]?.sourceName || 'Generated Risk Source';
            
            try {
              await this.dispatch(promoteScenarioToSaved({
                modelId: this.modelId,
                assessmentUuid: this.assessmentUuid,
                riskArea: areaName,
                sourceName: userSourceName,
                scenario: {
                  scenarioId: scenarioId,
                  scenarioText: scenario.scenarioText || scenario.title || 'Generated Risk Scenario',
                  rationale: scenario.rationale || scenario.evidence || '',
                  generationMethod: ragMetadata.method,
                  cataloguesUsed: ragMetadata.cataloguesUsed,
                  retrievedContext: ragMetadata.retrievedRisks,
                  // Include evaluation criteria from cause research
                  entity: scenario.entity,
                  intent: scenario.intent,
                  timing: scenario.timing,
                  other: scenario.other,
                  confidence: scenario.confidence
                },
                owner: this.owner || { name: 'System', email: 'system@riskgpt.com', picture: '' }
              }));
              console.log(`[RiskDiscoveryServiceV2] ✅ [${index + 1}/${scenarios.length}] Successfully promoted scenario`);
              return { success: true, index };
            } catch (promotionError) {
              console.error(`[RiskDiscoveryServiceV2] ❌ [${index + 1}/${scenarios.length}] Failed to promote scenario "${scenario.scenarioText?.slice(0, 40)}...":`, promotionError);
              return { 
                success: false, 
                index,
                scenario: scenario.scenarioText?.slice(0, 100),
                error: promotionError.message 
              };
            }
          });
          
          const promotionResults = await Promise.allSettled(promotionPromises);
          const promotedCount = promotionResults.filter(r => r.status === 'fulfilled' && r.value.success).length;
          const promotionErrors = promotionResults
            .filter(r => r.status === 'fulfilled' && !r.value.success)
            .map(r => r.value);
          
          const promotionDuration = Date.now() - promotionStartTime;
          
          if (promotionErrors.length > 0) {
            console.warn(`[RiskDiscoveryServiceV2] ⚠️ Promotion summary: ${promotedCount}/${scenarios.length} scenarios saved successfully, ${promotionErrors.length} failed (${promotionDuration}ms)`);
            console.warn('[RiskDiscoveryServiceV2] Failed promotions:', promotionErrors);
          } else {
            console.log(`[RiskDiscoveryServiceV2] ✅ All ${promotedCount} scenarios promoted successfully in ${promotionDuration}ms!`);
          }
          
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('scenarios', { 
              scenarios, 
              ragMetadata: {
                ...ragMetadata,
                inputCauses: relevantCausesForRAG.length,
                catalogueCount: ragMetadata.cataloguesUsed?.length || 0,
                catalogueNames: ragMetadata.cataloguesUsed || [],
                retrievedRisksCount: ragMetadata.retrievedRisks?.length || 0,
                generationSummary: `Generated ${scenarios.length} risk entries from ${relevantCausesForRAG.length} causes using ${ragMetadata.cataloguesUsed?.length || 0} risk catalogues`,
                riskBreakdown: scenarios.map(s => ({
                  title: s.scenarioText?.slice(0, 50) + '...',
                  source: s.source,
                  evaluation: `${s.entity}/${s.intent}/${s.timing}`,
                  confidence: s.confidence
                }))
              },
              totalGenerated: scenarios.length,
              totalPromoted: promotedCount,
              promotionErrors: promotionErrors.length,
              promotionDuration: promotionDuration,
              promotionRate: `${Math.round(scenarios.length / (promotionDuration / 1000) * 100) / 100} scenarios/sec`
            });
          }
        }
      } catch (scenError) {
        console.warn('[RiskDiscoveryServiceV2] RAG scenario generation failed:', scenError);
      }

      // Return comprehensive results (no assignments - direct to RAG)
      return {
        areaCard,
        selectedSources,
        causesBySource,
        relevantCausesForRAG,
        scenarios,
        ragMetadata,
        version: 'v2_optimized_rag_enhanced'
      };
      
    } catch (error) {
      console.error('[RiskDiscoveryServiceV2] Fatal error:', error);
      throw error;
    }
  }

  // Additional helper methods (same as V1)
  async retryArea(areaName, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg) {
    console.log(`[RiskDiscoveryServiceV2] Retrying area: ${areaName}`);
    return this.executeDiscovery(areaName, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, null);
  }

  async getAreaStatus(areaName) {
    // Implementation for checking area status
    return { status: 'pending', progress: 0 };
  }

  // Generate fallback causes when root cause analysis fails/times out
  generateFallbackCauses(selectedSources, areaDefinition) {
    const causesBySource = {};
    
    try {
      // Get risk sources from area definition
      const riskSources = Array.isArray(areaDefinition?.riskSources) ? areaDefinition.riskSources : [];
      
      selectedSources.forEach(selectedSource => {
        const sourceDef = riskSources.find(rs => rs.name === selectedSource.sourceName);
        if (sourceDef?.causes) {
          // Extract cause categories and create simplified structure
          const causes = [];
          Object.entries(sourceDef.causes).forEach(([category, categoryData]) => {
            const options = Array.isArray(categoryData.options) ? categoryData.options : [];
            // Take first 3 options as high-confidence fallback
            options.slice(0, 3).forEach(option => {
              causes.push({
                name: option,
                evidence: `Potential cause identified for ${selectedSource.sourceName}`,
                confidence: 0.7, // Default confidence for fallback
                category: category
              });
            });
          });
          
          if (causes.length > 0) {
            causesBySource[selectedSource.sourceName] = causes;
          }
        }
      });
      
      console.log(`[RiskDiscoveryServiceV2] Generated fallback causes for ${Object.keys(causesBySource).length} sources`);
    } catch (error) {
      console.error('[RiskDiscoveryServiceV2] Error generating fallback causes:', error);
    }
    
    return causesBySource;
  }

  // Generate simplified cause assignments when assignment API fails/times out
  generateSimplifiedAssignments(selectedSources, causesBySource) {
    const assignments = [];
    
    try {
      selectedSources.forEach(source => {
        const sourceCauses = causesBySource[source.sourceName] || [];
        if (sourceCauses.length > 0) {
          // Group causes by category
          const causesByCategory = {};
          sourceCauses.forEach(cause => {
            const category = cause.category || 'General';
            if (!causesByCategory[category]) {
              causesByCategory[category] = [];
            }
            causesByCategory[category].push(cause.name);
          });
          
          // Create assignments for each category
          Object.entries(causesByCategory).forEach(([category, selectedOptions]) => {
            assignments.push({
              sourceName: source.sourceName,
              category: category,
              selectedOptions: selectedOptions.slice(0, 3), // Limit to 3 causes
              confidence: 0.7 // Default confidence for fallback
            });
          });
        }
      });
      
      console.log(`[RiskDiscoveryServiceV2] Generated ${assignments.length} simplified assignments`);
    } catch (error) {
      console.error('[RiskDiscoveryServiceV2] Error generating simplified assignments:', error);
    }
    
    return assignments;
  }

  // Extract relevant causes from research results for RAG enhancement
  extractRelevantCausesForRAG(selectedSources, causesBySource, useCaseContext) {
    const relevantCauses = [];
    
    try {
      console.log(`[RiskDiscoveryServiceV2] SIMPLIFIED: Extracting evaluated causes from structure:`, Object.keys(causesBySource));
      console.log(`[RiskDiscoveryServiceV2] FULL causesBySource:`, JSON.stringify(causesBySource, null, 2));
      
      selectedSources.forEach(source => {
        console.log(`[RiskDiscoveryServiceV2] Processing source: ${source.sourceName} (confidence: ${source.aiConfidence})`);
        const sourceCauseData = causesBySource[source.sourceName];
        
        console.log(`[RiskDiscoveryServiceV2] Source data type:`, Array.isArray(sourceCauseData) ? 'Array' : typeof sourceCauseData);
        console.log(`[RiskDiscoveryServiceV2] Source data:`, sourceCauseData);
        
        if (Array.isArray(sourceCauseData)) {
          // Handle simplified structure with evaluated causes
          sourceCauseData.forEach((categoryResult, idx) => {
            console.log(`[RiskDiscoveryServiceV2] Category ${idx}: ${categoryResult.category}, ${categoryResult.researchedCauses?.length || 0} causes`);
            
            if (categoryResult.researchedCauses && categoryResult.researchedCauses.length > 0) {
              categoryResult.researchedCauses.forEach((cause, causeIdx) => {
                console.log(`[RiskDiscoveryServiceV2] Cause ${causeIdx}: "${cause.name}" (confidence: ${cause.confidence})`);
                
                if (cause.confidence >= 0.4) { // Match the filtering threshold
                  console.log(`[RiskDiscoveryServiceV2] ✅ ADDING relevant cause: ${cause.name}`);
                  
                  // Build dynamic evaluation context from all criteria in the cause object
                  const evaluationContext = Object.keys(cause)
                    .filter(key => !['name', 'description', 'evidence', 'confidence'].includes(key))
                    .map(key => `${key}: ${cause[key]}`)
                    .join('/');
                  
                  const causeData = {
                    source: source.sourceName,
                    sourceConfidence: source.aiConfidence,
                    causeName: cause.name,
                    description: cause.description,
                    evidence: cause.evidence,
                    confidence: cause.confidence,
                    category: categoryResult.category,
                    searchContext: `${source.sourceName} ${categoryResult.category}: ${cause.name}${evaluationContext ? ` (${evaluationContext})` : ''} - ${cause.evidence}`
                  };
                  
                  // Include all evaluation criteria dynamically
                  Object.keys(cause).forEach(key => {
                    if (!['name', 'description', 'evidence', 'confidence'].includes(key)) {
                      causeData[key] = cause[key];
                    }
                  });
                  
                  relevantCauses.push(causeData);
                } else {
                  console.log(`[RiskDiscoveryServiceV2] ❌ SKIPPING low confidence cause: ${cause.name} (${cause.confidence})`);
                }
              });
            } else {
              console.log(`[RiskDiscoveryServiceV2] No researchedCauses for category: ${categoryResult.category}`);
            }
          });
        } else {
          console.log(`[RiskDiscoveryServiceV2] ⚠️ Source data is not an array for: ${source.sourceName}`);
        }
      });
      
      // Sort by combined confidence (source confidence * cause confidence)
      relevantCauses.sort((a, b) => 
        (b.sourceConfidence * b.confidence) - (a.sourceConfidence * a.confidence)
      );
      
      // Limit to top 10 most relevant causes for RAG
      const topCauses = relevantCauses.slice(0, 10);
      
      console.log(`[RiskDiscoveryServiceV2] FINAL EXTRACTION: Filtered ${relevantCauses.length} total causes to ${topCauses.length} relevant for RAG`);
      console.log(`[RiskDiscoveryServiceV2] TOP CAUSES FOR RAG:`, topCauses.map(c => {
        const evaluationContext = Object.keys(c)
          .filter(key => !['source', 'sourceConfidence', 'causeName', 'description', 'evidence', 'confidence', 'category', 'searchContext'].includes(key))
          .map(key => c[key])
          .join('/');
        return `${c.causeName} (${c.category}${evaluationContext ? `/${evaluationContext}` : ''}, conf: ${c.confidence})`;
      }));
      
      return topCauses;
    } catch (error) {
      console.error('[RiskDiscoveryServiceV2] Error extracting relevant causes for RAG:', error);
      return [];
    }
  }
}